import { extend, useFrame, useThree } from '@react-three/fiber';
import ThreeCameraControls from 'camera-controls';
import * as holdEvent from 'hold-event';
import type { ReactNode, Ref } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box3,
  MathUtils,
  Matrix4,
  MOUSE,
  Quaternion,
  Raycaster,
  Sphere,
  Spherical,
  Vector2,
  Vector3,
  Vector4
} from 'three';

import { useStore } from '../store';
import { isServerRender } from '../utils/visibility';
import type { CameraControlsContextProps } from './useCameraControls';
import { CameraControlsContext } from './useCameraControls';

// Install the camera controls
// Use a subset for better three shaking
ThreeCameraControls.install({
  THREE: {
    MOUSE: MOUSE,
    Vector2: Vector2,
    Vector3: Vector3,
    Vector4: Vector4,
    Quaternion: Quaternion,
    Matrix4: Matrix4,
    Spherical: Spherical,
    Box3: Box3,
    Sphere: Sphere,
    Raycaster: Raycaster,
    MathUtils: {
      DEG2RAD: MathUtils?.DEG2RAD,
      clamp: MathUtils?.clamp
    }
  }
});

// Extend r3f with the new controls
extend({ ThreeCameraControls });

export type CameraMode = 'pan' | 'rotate' | 'orbit' | 'orthographic';

export interface CameraControlsProps {
  /**
   * Mode of the camera.
   */
  mode?: CameraMode;

  /**
   * Children symbols.
   */
  children?: ReactNode;

  /**
   * Animate transitions to centering.
   */
  animated?: boolean;

  /**
   * Whether the controls are enabled.
   */
  disabled?: boolean;

  /**
   * The maximum distance for the camera (perspective mode).
   */
  maxDistance?: number;

  /**
   * The minimum distance for the camera (perspective mode).
   */
  minDistance?: number;

  /**
   * The maximum zoom level for orthographic cameras.
   */
  maxZoom?: number;

  /**
   * The minimum zoom level for orthographic cameras.
   */
  minZoom?: number;
}

export type CameraControlsRef = CameraControlsContextProps;

export const CameraControls = forwardRef<
  CameraControlsRef,
  CameraControlsProps
>(
  (
    {
      mode = 'rotate',
      children,
      animated,
      disabled,
      minDistance = 1000,
      maxDistance = 50000,
      minZoom = 1,
      maxZoom = 100
    },
    ref: Ref<CameraControlsRef>
  ) => {
    const cameraRef = useRef<ThreeCameraControls | null>(null);
    const camera = useThree(state => state.camera);
    const gl = useThree(state => state.gl);
    const isOrbiting = mode === 'orbit';
    const setPanning = useStore(state => state.setPanning);
    const isDragging = useStore(state => state.draggingIds.length > 0);
    const cameraSpeedRef = useRef(0);
    const [controlMounted, setControlMounted] = useState<boolean>(false);

    useFrame((_state, delta) => {
      if (cameraRef.current?.enabled) {
        cameraRef.current?.update(delta);
      }

      if (isOrbiting) {
        cameraRef.current.azimuthAngle += 20 * delta * MathUtils.DEG2RAD;
      }
    }, -1);

    useEffect(() => () => cameraRef.current?.dispose(), []);

    const zoomIn = useCallback(() => {
      cameraRef.current?.zoom(camera.zoom / 2, animated);
    }, [animated, camera.zoom]);

    const zoomOut = useCallback(() => {
      cameraRef.current?.zoom(-camera.zoom / 2, animated);
    }, [animated, camera.zoom]);

    const dollyIn = useCallback(
      distance => {
        cameraRef.current?.dolly(distance, animated);
      },
      [animated]
    );

    const dollyOut = useCallback(
      distance => {
        cameraRef.current?.dolly(distance, animated);
      },
      [animated]
    );

    const panRight = useCallback(
      event => {
        if (!isOrbiting) {
          cameraRef.current?.truck(-0.03 * event.deltaTime, 0, animated);
        }
      },
      [animated, isOrbiting]
    );

    const panLeft = useCallback(
      event => {
        if (!isOrbiting) {
          cameraRef.current?.truck(0.03 * event.deltaTime, 0, animated);
        }
      },
      [animated, isOrbiting]
    );

    const panUp = useCallback(
      event => {
        if (!isOrbiting) {
          cameraRef.current?.truck(0, 0.03 * event.deltaTime, animated);
        }
      },
      [animated, isOrbiting]
    );

    const panDown = useCallback(
      event => {
        if (!isOrbiting) {
          cameraRef.current?.truck(0, -0.03 * event.deltaTime, animated);
        }
      },
      [animated, isOrbiting]
    );

    const onKeyDown = useCallback(
      event => {
        if (event.code === 'Space') {
          if (mode === 'rotate') {
            cameraRef.current.mouseButtons.left =
              ThreeCameraControls.ACTION.TRUCK;
          } else {
            cameraRef.current.mouseButtons.left =
              ThreeCameraControls.ACTION.ROTATE;
          }
        }
      },
      [mode]
    );

    const onKeyUp = useCallback(
      event => {
        if (event.code === 'Space') {
          if (mode === 'rotate') {
            cameraRef.current.mouseButtons.left =
              ThreeCameraControls.ACTION.ROTATE;
          } else {
            cameraRef.current.mouseButtons.left =
              ThreeCameraControls.ACTION.TRUCK;
          }
        }
      },
      [mode]
    );

    const [keyControls, setKeyControls] = useState<{
      leftKey: holdEvent.KeyboardKeyHold;
      rightKey: holdEvent.KeyboardKeyHold;
      upKey: holdEvent.KeyboardKeyHold;
      downKey: holdEvent.KeyboardKeyHold;
    } | null>(null);

    useEffect(() => {
      // Only initialize on client side
      if (!isServerRender) {
        setKeyControls({
          leftKey: new holdEvent.KeyboardKeyHold('ArrowLeft', 100),
          rightKey: new holdEvent.KeyboardKeyHold('ArrowRight', 100),
          upKey: new holdEvent.KeyboardKeyHold('ArrowUp', 100),
          downKey: new holdEvent.KeyboardKeyHold('ArrowDown', 100)
        });
      }
    }, []);

    useEffect(() => {
      if (!disabled && keyControls) {
        keyControls.leftKey.addEventListener('holding', panLeft);
        keyControls.rightKey.addEventListener('holding', panRight);
        keyControls.upKey.addEventListener('holding', panUp);
        keyControls.downKey.addEventListener('holding', panDown);

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
      }

      return () => {
        if (keyControls) {
          keyControls.leftKey.removeEventListener('holding', panLeft);
          keyControls.rightKey.removeEventListener('holding', panRight);
          keyControls.upKey.removeEventListener('holding', panUp);
          keyControls.downKey.removeEventListener('holding', panDown);

          window.removeEventListener('keydown', onKeyDown);
          window.removeEventListener('keyup', onKeyUp);
        }
      };
    }, [
      disabled,
      onKeyDown,
      onKeyUp,
      panDown,
      panLeft,
      panRight,
      panUp,
      keyControls
    ]);

    useEffect(() => {
      const isOrthographic = mode === 'orthographic';

      if (disabled) {
        cameraRef.current.mouseButtons.left = ThreeCameraControls.ACTION.NONE;
        cameraRef.current.mouseButtons.middle = ThreeCameraControls.ACTION.NONE;
        cameraRef.current.mouseButtons.wheel = ThreeCameraControls.ACTION.NONE;
      } else {
        cameraRef.current.mouseButtons.left = ThreeCameraControls.ACTION.TRUCK;
        cameraRef.current.mouseButtons.middle =
          ThreeCameraControls.ACTION.TRUCK;
        cameraRef.current.mouseButtons.wheel = isOrthographic
          ? ThreeCameraControls.ACTION.ZOOM
          : ThreeCameraControls.ACTION.DOLLY;
      }

      // For orthographic cameras, use dedicated zoom props
      if (isOrthographic && cameraRef.current) {
        cameraRef.current.maxZoom = maxZoom;
        cameraRef.current.minZoom = minZoom;
      }
    }, [disabled, mode, minZoom, maxZoom]);

    useEffect(() => {
      const onControl = () => setPanning(true);
      const onControlEnd = () => setPanning(false);

      const ref = cameraRef.current;
      if (ref) {
        ref.addEventListener('control', onControl);
        ref.addEventListener('controlend', onControlEnd);
      }

      return () => {
        if (ref) {
          ref.removeEventListener('control', onControl);
          ref.removeEventListener('controlend', onControlEnd);
        }
      };
    }, [cameraRef, setPanning]);

    useEffect(() => {
      // If a node is being dragged, disable the camera controls
      if (isDragging) {
        cameraRef.current.mouseButtons.left = ThreeCameraControls.ACTION.NONE;
        cameraRef.current.touches.one = ThreeCameraControls.ACTION.NONE;
      } else {
        if (mode === 'rotate') {
          cameraRef.current.mouseButtons.left =
            ThreeCameraControls.ACTION.ROTATE;
          cameraRef.current.touches.one =
            ThreeCameraControls.ACTION.TOUCH_ROTATE;
        } else {
          cameraRef.current.touches.one =
            ThreeCameraControls.ACTION.TOUCH_TRUCK;
          cameraRef.current.mouseButtons.left =
            ThreeCameraControls.ACTION.TRUCK;
        }
      }
    }, [isDragging, mode]);

    const values = useMemo(
      () => ({
        controls: cameraRef.current,
        zoomIn: () => zoomIn(),
        zoomOut: () => zoomOut(),
        dollyIn: (distance = 1000) => dollyIn(distance),
        dollyOut: (distance = -1000) => dollyOut(distance),
        panLeft: (deltaTime = 100) => panLeft({ deltaTime }),
        panRight: (deltaTime = 100) => panRight({ deltaTime }),
        panDown: (deltaTime = 100) => panDown({ deltaTime }),
        panUp: (deltaTime = 100) => panUp({ deltaTime }),
        resetControls: (animated?: boolean) =>
          cameraRef.current?.normalizeRotations().reset(animated),
        freeze: () => {
          // Save the current speed
          if (cameraRef.current.truckSpeed) {
            cameraSpeedRef.current = cameraRef.current.truckSpeed;
          }
          cameraRef.current.truckSpeed = 0;
        },
        unFreeze: () => (cameraRef.current.truckSpeed = cameraSpeedRef.current)
      }),
      // eslint-disable-next-line
      [zoomIn, zoomOut, panLeft, panRight, panDown, panUp, cameraRef.current]
    );

    useImperativeHandle(ref, () => values);

    return (
      <CameraControlsContext.Provider value={values}>
        <threeCameraControls
          ref={controls => {
            cameraRef.current = controls;
            if (!controlMounted) {
              // Update the state when the controls are mounted to notify about it component that using that controls
              setControlMounted(true);
            }
          }}
          args={[camera, gl.domElement]}
          smoothTime={0.1}
          minDistance={minDistance}
          dollyToCursor
          maxDistance={maxDistance}
        />
        {children}
      </CameraControlsContext.Provider>
    );
  }
);
